// Core packages
import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

// Third party packages
import { TranslateService } from "@ngx-translate/core";
import { CookieService } from "ngx-cookie-service";
import { Subscription } from "rxjs";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { CompaniesService } from "src/app/modules/companies/companies.service";
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { PropertyInterface } from "src/app/shared/models/property/property.interface";
import { Property } from "src/app/shared/models/property/property.model";
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { ThemeService } from "src/app/shared/services/theme.service";

@Component({
  selector: "app-property-notes-contractual",
  templateUrl: "./property-notes-contractual.page.html",
})
export class PropertyNotesContractualAgreements implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];

  loggedUser: IUser;
  currentRole: any;
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  // Could not be "Property" type because in "get" method in the back-end
  // We are populating some fields, so the model is not the same!
  public property: Property = new Property();
  propertyData: PropertyInterface;
  propertyId = this.activatedRoute.snapshot.params.propertyId;
  companyId = '';

  loggedUserShared = false;
  loggedUserMaster = false;

  masterOnlySuppliersAndNfcTag = false;
  masterOnlySuppliers = false;
  masterOnlyNfcTag = false;

  /**
   * Class constructor
   */
  constructor(
    private configService: ConfigService,
    private activatedRoute: ActivatedRoute,
    private propertiesService: PropertiesService,
    private router: Router,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private authService: AuthService,
    private cookieService: CookieService,
    private companiesService: CompaniesService,
  ) { }

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(
      this.translateService.instant("properties.detail.pageTitle")
    );

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => {
        this.loggedUser = res;
        if (!this.loggedUser || !Array.isArray(this.loggedUser.userRoles)) {
          return;
        }
        this.currentRole = this.loggedUser.userRoles.find((obj: any) => {
          return (
            obj._id.toString() ===
            this.authService.loggedUserRequestedRole$.getValue().toString()
          );
        });
        if (!this.currentRole || !this.currentRole.role) {
          return;
        }
        // Get logged user's role data
        if (this.currentRole.role.type === "shared") {
          this.loggedUserShared = true;
        }
        if (
          this.currentRole.role.type === "master" ||
          this.currentRole.role.type === "collaborator"
        ) {
          this.loggedUserMaster = true;
          if (this.currentRole.company && this.currentRole.company._id !== "") {
            this.subscriptions.push(
              this.companiesService
                .get(this.currentRole.company._id)
                .subscribe((companyRes) => {
                  if (
                    companyRes.status &&
                    companyRes.data &&
                    companyRes.data.subscription
                  ) {
                    const subscription = companyRes.data.subscription;
                    if (
                      !subscription.documentUpload &&
                      subscription.nfcAccess &&
                      subscription.suppliers
                    ) {
                      this.masterOnlySuppliersAndNfcTag = true;
                    } else if (
                      !subscription.documentUpload &&
                      !subscription.nfcAccess &&
                      subscription.suppliers
                    ) {
                      this.masterOnlySuppliers = true;
                    } else if (
                      !subscription.documentUpload &&
                      subscription.nfcAccess &&
                      !subscription.suppliers
                    ) {
                      this.masterOnlyNfcTag = true;
                    }
                  }
                })
            );
          }
        }
      })
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // Get requested property data
    this.subscriptions.push(
      this.propertiesService
        .get(this.propertyId)
        .subscribe(
          (res) => {
            if (res.status) {
              this.property.setData(res.data);
              this.propertyData = JSON.parse(JSON.stringify(res.data));

              this.companyId = this.property.get("admin.company._id");
              if (typeof this.companyId === "undefined") {
                this.companyId = this.property.get("admin.company");
              }
              return;
            }

            // Something bad happened
            // redirect user to home
            this.router.navigate([this.lang, "app", "properties", "list"]);
          },
          (err) => {
            // if we came here the requested property does not exist
            this.router.navigate([this.lang, "errors", "404"]);
          }
        )
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
